/**
 * MED-938 / E3.5 — "Needs Attention" section. Replaces the legacy Action Items
 * section. Shows a count badge and one row per pending item. Standard items
 * (reflection, feedback), peer-leader prompts (states 1/2/5), and the carried-
 * over 1:1/check-in scheduling items all share one row pattern. The whole
 * section is omitted when there is nothing pending (AC-NA4).
 */
import { Badge, Box, Button, Group, Stack, Text, ThemeIcon } from '@mantine/core';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Calendar,
  MessageSquare,
  AlertTriangle,
  Mic,
  Mail,
} from 'react-feather';
import type { NeedsAttentionItem } from './dashboardRefreshUtils.js';

interface NeedsAttentionSectionProps {
  items: NeedsAttentionItem[];
  onScheduleCoaching: (coachId: string) => void;
  onScheduleCheckin: (coachId: string, huddleType: 'intro' | 'midpoint_checkin') => void;
}

function reflectionPath(sessionId: string): string {
  return `/participant/sessions/${sessionId}/reflection`;
}

function postSessionPath(sessionId: string): string {
  return `/participant/sessions/${sessionId}/post-session`;
}

// MED-1145: the peer-leader post-session page is cohort-scoped (distinct from the
// non-leader feedback page above). Matches the App.tsx route + the coach precedent.
function peerLeaderPostSessionPath(cohortId: string, sessionId: string): string {
  return `/participant/cohorts/${cohortId}/sessions/${sessionId}/post-session`;
}

function ItemIcon({ item }: { item: NeedsAttentionItem }) {
  switch (item.kind) {
    case 'peer_leader_backup':
      return (
        <ThemeIcon size={36} radius="md" color="dangerRed" variant="light">
          <AlertTriangle size={18} />
        </ThemeIcon>
      );
    case 'peer_leader_lead':
      return (
        <ThemeIcon size={36} radius="md" color="medleyGreen" variant="light">
          <Mic size={18} />
        </ThemeIcon>
      );
    case 'reflection':
      return (
        <ThemeIcon size={36} radius="md" color="warningAmber" variant="light">
          <BookOpen size={18} />
        </ThemeIcon>
      );
    case 'feedback':
      return (
        <ThemeIcon size={36} radius="md" color="surveyPurple" variant="light">
          <MessageSquare size={18} />
        </ThemeIcon>
      );
    case 'return_to_post_session':
      return (
        <ThemeIcon size={36} radius="md" color="medleyGreen" variant="light">
          <Mail size={18} />
        </ThemeIcon>
      );
    default:
      return (
        <ThemeIcon size={36} radius="md" color="gray" variant="light">
          <Calendar size={18} />
        </ThemeIcon>
      );
  }
}

function ItemCta({
  item,
  onScheduleCoaching,
  onScheduleCheckin,
}: NeedsAttentionSectionProps & { item: NeedsAttentionItem }) {
  switch (item.kind) {
    case 'peer_leader_backup':
      return (
        <Button
          component={Link}
          to={reflectionPath(item.sessionId)}
          size="xs"
          color="dangerRed"
          rightSection={<ArrowRight size={14} />}
        >
          Review
        </Button>
      );
    case 'peer_leader_lead':
      return (
        <Button
          component={Link}
          to={reflectionPath(item.sessionId)}
          size="xs"
          color="medleyGreen"
        >
          Start Session Prep
        </Button>
      );
    case 'reflection':
      // MED-1141 (AC4): the reflection CTA reads "Complete" (not "Start") so the
      // label matches the Upcoming card and everywhere else completion surfaces.
      // This item only renders while the reflection is incomplete, so "Complete"
      // is correct whether it's untouched or partially filled.
      return (
        <Button
          component={Link}
          to={reflectionPath(item.sessionId)}
          size="xs"
          style={{ backgroundColor: '#2D4739' }}
        >
          Complete
        </Button>
      );
    case 'feedback':
      return (
        <Button
          component={Link}
          to={postSessionPath(item.sessionId)}
          size="xs"
          style={{ backgroundColor: '#2D4739' }}
        >
          Complete
        </Button>
      );
    case 'return_to_post_session':
      // MED-1145 (AC2): routes back into the fully-functional post-session page
      // (recap composer + Send Recap modal). No read-only / send-once state.
      return (
        <Button
          component={Link}
          to={peerLeaderPostSessionPath(item.cohortId, item.sessionId)}
          size="xs"
          color="medleyGreen"
          rightSection={<ArrowRight size={14} />}
        >
          Open Recap
        </Button>
      );
    case 'schedule_coaching':
      return (
        <Button
          size="xs"
          color="medleyGreen"
          disabled={item.disabled}
          onClick={() => onScheduleCoaching(item.coachId)}
        >
          Schedule
        </Button>
      );
    case 'schedule_checkin':
      return (
        <Button
          size="xs"
          color="medleyGreen"
          disabled={item.disabled}
          onClick={() => onScheduleCheckin(item.coachId, item.huddleType)}
        >
          Schedule
        </Button>
      );
  }
}

function NeedsAttentionRow(props: NeedsAttentionSectionProps & { item: NeedsAttentionItem }) {
  const { item } = props;
  const emphasized = item.kind === 'peer_leader_backup';
  return (
    <Box
      p="md"
      style={{
        backgroundColor: emphasized ? 'rgba(214, 69, 69, 0.06)' : 'white',
        border: emphasized ? '1px solid rgba(214, 69, 69, 0.35)' : '1px solid #ECE9E3',
        borderRadius: 8,
      }}
    >
      <Group justify="space-between" wrap="nowrap" gap="md">
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
          <ItemIcon item={item} />
          <Box style={{ minWidth: 0 }}>
            <Text size="sm" fw={600} truncate>
              {item.title}
            </Text>
            <Text size="xs" c="dimmed" truncate>
              {item.subtitle}
            </Text>
          </Box>
        </Group>
        <Box style={{ flexShrink: 0 }}>
          <ItemCta {...props} />
        </Box>
      </Group>
    </Box>
  );
}

export function NeedsAttentionSection(props: NeedsAttentionSectionProps) {
  const { items } = props;
  if (items.length === 0) return null;

  return (
    <Box mb="xl">
      <Group gap="xs" mb="sm">
        <Text fw={700} size="md">
          Needs Attention
        </Text>
        <Badge
          size="sm"
          circle
          variant="filled"
          style={{ backgroundColor: '#2D4739', color: 'white' }}
        >
          {items.length}
        </Badge>
      </Group>
      <Stack gap="sm">
        {items.map(item => (
          <NeedsAttentionRow key={item.key} {...props} item={item} />
        ))}
      </Stack>
    </Box>
  );
}
